import { assign, emit, fromPromise, setup } from "xstate";

import { auditService } from "@/services";

import type WaveSurfer from "wavesurfer.js";
import type Regions from "wavesurfer.js/dist/plugins/regions.js";

type VoiceMachineContext = {
  surfer: WaveSurfer | null;
  error: string;
  regions: Partial<Record<KnownRegionTypes, Regions>> | null;
  duration: number;
  elapsed: number;
  speed: number;
  volume: number;
  metadata: {
    client: string;
    brand: string;
    caseReference: string;
    fileReference: string;
  };
};

type VoiceMachineEvents =
  | { type: "PLAYER_PROCESS"; value: number }
  | { type: "PLAYER_RE_RENDER" }
  | { type: "PLAYER_PLAY" }
  | { type: "PLAYER_PAUSE" }
  | { type: "PLAYER_DESTROY" }
  | { type: "PLAYER_SKIP_FORWARD" }
  | { type: "PLAYER_SKIP_BACKWARD" }
  | { type: "PLAYER_CHANGE_VOLUME"; value: number }
  | { type: "PLAYER_CHANGE_POSITION"; seconds: string | number }
  | { type: "PLAYER_CHANGE_SPEED"; value: number }
  | { type: "PLAYER_CHANGE_DURATION"; value: number }
  | {
      type: "PLAYER_ADD_REGIONS_PLUGIN";
      name: keyof NonNullable<VoiceMachineContext["regions"]>;
      regions: Regions;
    }
  | {
      type: "PLAYER_LOAD_AUDIO";
      url: string;
      peaks: number[];
      metadata: Record<"client" | "brand" | "caseReference" | "fileReference", string>;
    }
  | { type: "PLAYER_SET_SURFER"; surfer: WaveSurfer | null }
  | { type: "PLAYER_ERROR"; message: string }
  | { type: "PLAYER_IDLE" }
  | { type: "PLAYER_INC_SPEED" }
  | { type: "PLAYER_DEC_SPEED" };

type VoiceMachineEmits = { type: "PLAYER_READY" };

type VoiceMachineInitialInput = VoiceMachineContext["metadata"];

export type KnownRegionTypes = "pauses" | "marks" | "formFieldEdits" | "redactions";

export const voicePlayerMachine = setup({
  types: {
    context: {} as VoiceMachineContext,
    events: {} as VoiceMachineEvents,
    input: {} as VoiceMachineInitialInput,
    emitted: {} as VoiceMachineEmits,
  },
  actors: {
    wavesurferLoadAudio: fromPromise<unknown, { url: string; peaks: number[]; wavesurfer: WaveSurfer }>(async (ctx) => {
      const { url, peaks, wavesurfer } = ctx.input;
      if (url == null) throw new Error("Missing input");

      await wavesurfer.load(url, peaks.length > 0 ? [peaks] : undefined);
    }),
  },
  actions: {
    PLAYER_PAUSE: ({ context }) => {
      if (!context.surfer?.isPlaying()) return;

      auditService.PostAuditPause(
        context.metadata.client,
        context.metadata.brand,
        context.metadata.caseReference,
        context.metadata.fileReference,
      );
      context.surfer.pause();
    },
    PLAYER_PLAY: ({ context }) => {
      if (context.surfer == null || context.surfer.isPlaying()) return;

      auditService.PostAuditPlay(
        context.metadata.client,
        context.metadata.brand,
        context.metadata.caseReference,
        context.metadata.fileReference,
      );
      context.surfer.play();
    },
  },
  guards: {
    hasSurfer: ({ context }) => context.surfer != null,
  },
}).createMachine({
  id: "VoicePlayerMachine",
  context: ({ input }) => ({
    surfer: null,
    regions: null,
    duration: 0,
    elapsed: 0,
    speed: 1,
    volume: 1,
    error: "",
    metadata: {
      client: input.client,
      brand: input.brand,
      caseReference: input.caseReference,
      fileReference: input.fileReference,
    },
  }),
  initial: "init",
  states: {
    init: {
      entry: "PLAYER_PAUSE",
      on: {
        PLAYER_SET_SURFER: {
          actions: assign(({ event, self }) => {
            if (event.surfer != null) {
              event.surfer.on("timeupdate", (value) => self.send({ type: "PLAYER_PROCESS", value }));
              event.surfer.on("pause", () => self.send({ type: "PLAYER_PAUSE" }));
            }

            return { surfer: event.surfer };
          }),
        },
        PLAYER_LOAD_AUDIO: {
          target: "loading",
          guard: "hasSurfer",
          actions: assign({ metadata: ({ event }) => event.metadata }),
        },
      },
    },
    loading: {
      invoke: {
        src: "wavesurferLoadAudio",
        onDone: { target: "ready" },
        onError: {
          target: "error",
          actions: assign({
            error: ({ event }) => {
              if (typeof event.error === "object") {
                // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/prefer-optional-chain
                const message = (event.error ?? {}).toString();
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                if (message !== {}.toString()) return message;
              }

              return new Error("Unknown").toString();
            },
          }),
        },
        input: ({ context, event }) => {
          if (event.type !== "PLAYER_LOAD_AUDIO" || context.surfer == null) return {} as never;
          return { url: event.url, peaks: event.peaks, wavesurfer: context.surfer };
        },
      },
      on: {
        PLAYER_ERROR: {
          target: "error",
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    ready: {
      initial: "idle",
      entry: [
        assign({ elapsed: 0, duration: (ctx) => ctx.context.surfer?.getDuration() ?? 0 }),
        emit({ type: "PLAYER_READY" }),
      ],
      on: {
        PLAYER_DESTROY: {
          target: "init",
          actions: assign(({ context }) => {
            context.surfer?.destroy();
            return { surfer: null };
          }),
        },
        PLAYER_PROCESS: {
          actions: assign({ elapsed: ({ event }) => event.value }),
        },
        PLAYER_RE_RENDER: {
          actions: ({ context }) => {
            if (context.surfer == null) return;
            context.surfer.setOptions(context.surfer.options);
          },
        },
        PLAYER_SKIP_FORWARD: {
          actions: ({ context }) => {
            if (context.surfer == null) return;
            context.surfer.setTime(Math.min(context.surfer.getCurrentTime() + 2, context.surfer.getDuration()));
          },
        },
        PLAYER_SKIP_BACKWARD: {
          actions: ({ context }) => {
            if (context.surfer == null) return;
            context.surfer.setTime(Math.max(0, context.surfer.getCurrentTime() - 2));
          },
        },
        PLAYER_INC_SPEED: {
          actions: [
            assign({
              speed: ({ context }) => (context.speed < 2 ? context.speed + 0.25 : context.speed),
            }),
            ({ context }) => context.surfer?.setPlaybackRate(context.speed),
          ],
        },
        PLAYER_DEC_SPEED: {
          actions: [
            assign({
              speed: ({ context }) => (context.speed > 0.25 ? context.speed - 0.25 : context.speed),
            }),
            ({ context }) => context.surfer?.setPlaybackRate(context.speed),
          ],
        },
        PLAYER_CHANGE_SPEED: {
          actions: [
            assign({ speed: ({ event }) => event.value }),
            ({ context, event }) => context.surfer?.setPlaybackRate(event.value),
          ],
        },
        PLAYER_CHANGE_VOLUME: {
          actions: [
            assign({ volume: ({ event }) => event.value }),
            ({ context, event }) => context.surfer?.setVolume(event.value),
          ],
        },
        PLAYER_CHANGE_POSITION: {
          actions: ({ context, event }) =>
            context.surfer?.setTime(Math.min(+event.seconds, context.surfer.getDuration())),
        },
        PLAYER_ADD_REGIONS_PLUGIN: {
          actions: assign(({ context, event }) => {
            if (context.surfer == null) return {};

            context.surfer.registerPlugin(event.regions);
            return { regions: { ...context.regions, [event.name]: event.regions } };
          }),
        },
      },
      states: {
        idle: {
          on: { PLAYER_PLAY: { target: "playing" } },
        },
        playing: {
          entry: "PLAYER_PLAY",
          exit: "PLAYER_PAUSE",
          on: { PLAYER_PAUSE: { target: "idle" } },
        },
      },
    },
    error: {},
  },
  on: {
    PLAYER_IDLE: { target: ".init" },
    PLAYER_CHANGE_DURATION: { actions: assign({ duration: ({ event }) => event.value }) },
  },
});
